import { useState } from "react";
import { NavLink, useLocation, Outlet } from "react-router-dom";
import {
  SquaresFour as LayoutDashboard, Broadcast as Radio, Users, FileText,
  ChatsCircle as MessagesSquare, PaperPlaneTilt as Send, Key as KeyRound,
  Lightning as Webhook, List as Menu, SignOut as LogOut, ShieldCheck,
  UserGear as UserCog, Database, Bell, Question,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/channels", label: "Channel", icon: Radio },
  { to: "/contacts", label: "Contacts", icon: Users },
  { to: "/templates", label: "Template", icon: FileText },
  { to: "/conversations", label: "Conversation", icon: MessagesSquare },
  { to: "/messages", label: "Message", icon: Send },
  { to: "/access-tokens", label: "Access Token", icon: KeyRound },
  { to: "/autopilots", label: "Autopilot", icon: Webhook },
];

const ADMIN_NAV = [
  { to: "/admin/users", label: "Users", icon: UserCog },
  { to: "/admin/channels", label: "Channels", icon: Database },
  { to: "/admin/templates", label: "Templates", icon: FileText },
  { to: "/admin/messages", label: "Messages", icon: MessagesSquare },
];

function NavItem({ item, onNavigate }) {
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-[4px] text-sm transition-colors ${
          isActive
            ? "bg-[#FFDA1A] text-[#111111] font-bold"
            : "text-white/75 font-medium hover:bg-white/10 hover:text-white"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <item.icon className="w-[18px] h-[18px]" weight={isActive ? "fill" : "regular"} />
          {item.label}
        </>
      )}
    </NavLink>
  );
}

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  return (
    <div className="flex flex-col h-full w-full">
      <div className="h-16 flex items-center px-6 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-[4px] bg-[#FFDA1A] flex items-center justify-center text-[#003399] font-heading font-extrabold text-base">S</div>
          <span className="font-heading text-xl font-bold tracking-tight text-white">Serabi</span>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV.map((item) => <NavItem key={item.to} item={item} onNavigate={onNavigate} />)}
        {isAdmin && (
          <>
            <div className="px-3 pt-5 pb-2 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-[#FFDA1A]" weight="fill" />
              <span className="text-xs uppercase tracking-[0.15em] font-semibold text-white/40">Admin</span>
            </div>
            {ADMIN_NAV.map((item) => <NavItem key={item.to} item={item} onNavigate={onNavigate} />)}
          </>
        )}
      </nav>
      <div className="p-3 border-t border-white/10 flex-shrink-0">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white">
            {(user?.name || "U").charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
            <p className="text-xs text-white/50 truncate">{user?.email}</p>
          </div>
          <button onClick={logout} data-testid="logout-button" className="text-white/50 hover:text-[#FFDA1A] p-1.5 rounded-[4px] hover:bg-white/10 transition-colors">
            <LogOut className="w-4 h-4" weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}

function HeaderProfile() {
  const { user } = useAuth();
  const displayName = user?.name || "User";
  const roleLabel = user?.role === "admin" ? "Admin Access" : "User Access";

  return (
    <div className="ml-auto flex items-center gap-2 sm:gap-4">
      <button className="hidden sm:flex w-9 h-9 items-center justify-center rounded-[4px] text-[#484848] hover:bg-[#F5F5F5] hover:text-[#003399] transition-colors" aria-label="Notifikasi">
        <Bell className="w-5 h-5" weight="fill" />
      </button>
      <button className="hidden sm:flex w-9 h-9 items-center justify-center rounded-[4px] text-[#484848] hover:bg-[#F5F5F5] hover:text-[#003399] transition-colors" aria-label="Bantuan">
        <Question className="w-5 h-5" weight="fill" />
      </button>
      <div className="hidden sm:block h-8 w-px bg-[#E8E8E8]" />
      <div className="flex items-center gap-3 min-w-0">
        <div className="hidden sm:block text-right min-w-0">
          <p className="text-sm font-bold text-[#061E67] truncate max-w-[160px]">{displayName}</p>
          <p className="text-[10px] uppercase tracking-[0.08em] font-semibold text-[#767676]">{roleLabel}</p>
        </div>
        <div className="w-10 h-10 rounded-[4px] bg-[#003399] text-white flex items-center justify-center font-heading text-sm font-extrabold ring-2 ring-[#FFDA1A]">
          {displayName.charAt(0).toUpperCase()}
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const current = [...NAV, ...ADMIN_NAV].find((n) => location.pathname.startsWith(n.to));
  const isConversation = location.pathname.startsWith("/conversations");

  return (
    <div className="flex h-screen bg-[#F5F5F5] overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-[#003399] flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[80] lg:hidden">
          <div className="absolute inset-0 bg-[#111111]/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-[#003399] animate-drawer-in-left">
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white z-40 flex items-center px-4 lg:px-6 flex-shrink-0 gap-3">
          <button className="lg:hidden text-[#484848]" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
            <Menu className="w-5 h-5" weight="bold" />
          </button>
          <span className="w-1.5 h-6 bg-[#FFDA1A] rounded-full" />
          <h2 className="font-heading text-lg font-bold text-[#111111]">{current?.label || "Serabi"}</h2>
          <HeaderProfile />
        </header>
        <main className={isConversation ? "flex-1 overflow-hidden" : "flex-1 overflow-y-auto p-6 md:p-8"}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
